/**
 * ConversationHistorySidebar - Sidebar showing user's conversation history
 * Allows users to resume previous conversations, similar to ChatGPT's interface.
 * Can be toggled via VITE_ENABLE_CONVERSATION_HISTORY environment variable.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  X,
  Plus,
  Clock,
  Loader2,
} from 'lucide-react';
import { getMyConversations, deactivateConversation, UserConversation } from '../../lib/api/chat';
import leblanc from '../../images/leblanc.png';

interface ConversationHistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onNewConversation: () => void;
}

export function ConversationHistorySidebar({
  isOpen,
  onClose,
  onNewConversation,
}: ConversationHistorySidebarProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { conversationId: currentConversationId } = useParams<{
    conversationId: string;
  }>();

  // Fetch user's conversations
  const {
    data: conversationsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['my-conversations'],
    queryFn: () => getMyConversations(0, 50),
    refetchOnWindowFocus: true,
    staleTime: 30000, // 30 seconds
    enabled: isOpen, // Only fetch when sidebar is open
  });

  // Mutation to deactivate a conversation
  const deactivateMutation = useMutation({
    mutationFn: deactivateConversation,
    onSuccess: (_, conversationId) => {
      // Refresh the conversations list
      queryClient.invalidateQueries({ queryKey: ['my-conversations'] });
      // If the deactivated conversation is currently open, navigate to new chat
      if (currentConversationId === conversationId) {
        onNewConversation();
        navigate('/chat');
      }
    },
  });

  // Handle deactivating a conversation
  const handleDeactivate = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation(); // Prevent triggering the conversation selection
    deactivateMutation.mutate(conversationId);
  };

  // Don't render if closed
  if (!isOpen) return null;

  const conversations = conversationsData?.conversations || [];

  // Format date for display - compare dates at day level to avoid timezone issues
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();

    // Compare at day level in local timezone to avoid UTC offset issues
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffMs = nowDay.getTime() - dateDay.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return "Aujourd'hui";
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    } else {
      return date.toLocaleDateString('fr-CA', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  // Group conversations by date
  const groupedConversations = conversations.reduce(
    (groups, conv) => {
      const dateLabel = formatDate(conv.started_at);
      if (!groups[dateLabel]) {
        groups[dateLabel] = [];
      }
      groups[dateLabel].push(conv);
      return groups;
    },
    {} as Record<string, UserConversation[]>
  );

  const handleSelectConversation = (conversationId: string) => {
    navigate(`/chat/${conversationId}`);
    // Keep sidebar open - user can close it manually with X button
  };

  const handleNewConversation = () => {
    onNewConversation();
    navigate('/chat');
    // Keep sidebar open - user can close it manually with X button
  };

  return (
    <>
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-80 z-40 transition-transform duration-300 ease-in-out">
        {/* Sidebar background with glass effect */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-xl border-r border-white/10" />

        {/* Sidebar content */}
        <div className="relative h-full flex flex-col">
          {/* Branding Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <Link
                to="https://www.leblanc-associes.com/"
                className="flex items-center gap-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="text-xs font-bold text-white/70">Propulsé par</span>
                <img src={leblanc} alt="LeBlanc" className="h-6 w-auto" />
              </Link>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* History Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <h2 className="text-white/90 font-medium text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Historique des conversations
            </h2>
          </div>

          {/* New conversation button */}
          <div className="p-2">
            <button
              onClick={handleNewConversation}
              className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/90 transition-colors"
              title="Nouvelle conversation"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">Nouvelle conversation</span>
            </button>
          </div>

        {/* Conversations list */}
          <div className="flex-1 overflow-y-auto px-2 pb-4 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-white/50 animate-spin" />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-white/50 text-sm">
                <p>Erreur de chargement</p>
                <button
                  onClick={() => refetch()}
                  className="mt-2 text-white/70 hover:text-white underline"
                >
                  Réessayer
                </button>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-white/50 text-sm">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucune conversation</p>
                <p className="text-xs mt-1 opacity-75">
                  Commencez une nouvelle conversation
                </p>
              </div>
            ) : (
              Object.entries(groupedConversations).map(([dateLabel, convs]) => (
                <div key={dateLabel} className="mb-4">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-white/50 text-xs font-medium uppercase tracking-wide">
                    <Clock className="w-3 h-3" />
                    {dateLabel}
                  </div>
                  <div className="space-y-1">
                    {convs.map((conv) => (
                      <div
                        key={conv.id}
                        className={`w-full text-left p-2.5 rounded-lg transition-all group relative ${
                          currentConversationId === conv.id
                            ? 'bg-white/20 text-white'
                            : 'hover:bg-white/10 text-white/80'
                        }`}
                      >
                        <button
                          onClick={() => handleSelectConversation(conv.id)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start gap-2 pr-6">
                            <MessageSquare
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                currentConversationId === conv.id
                                  ? 'text-white'
                                  : 'text-white/50 group-hover:text-white/70'
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {conv.first_message_snippet || 'Nouvelle conversation'}
                              </p>
                              <p className="text-xs text-white/50 mt-0.5">
                                {conv.message_count} message
                                {conv.message_count !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </button>
                        {/* Deactivate button */}
                        <button
                          onClick={(e) => handleDeactivate(e, conv.id)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/20 text-white/50 hover:text-white transition-all"
                          title="Retirer de l'historique"
                          disabled={deactivateMutation.isPending}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
